import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

// ---------------------------------------------------------------------------
// PageHeader
// ---------------------------------------------------------------------------

/**
 * Pins a page's title row, and whatever filters are passed as children, under
 * the top bar. It publishes its own height as `--page-head-h` on its parent so
 * sticky table headers below stack under it instead of behind it.
 */
export function PageHead({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const host = el?.parentElement as (HTMLElement & { __pageHead?: object }) | null | undefined;
    if (!el || !host) return;
    // Two pages can share a host across a route change, so only the head that
    // published the height clears it.
    const owner = {};
    host.__pageHead = owner;
    const sync = () => host.style.setProperty("--page-head-h", `${Math.round(el.getBoundingClientRect().height)}px`);
    const release = () => {
      if (host.__pageHead !== owner) return;
      delete host.__pageHead;
      host.style.removeProperty("--page-head-h");
    };
    sync();
    if (typeof ResizeObserver === "undefined") return release;
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      release();
    };
  }, []);
  return (
    <div className="page-head" ref={ref}>
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <PageHead>
      <div className="page-header">
        <div className="page-header-main">
          {back}
          <div>
            <h1>{title}</h1>
            {subtitle && <p className="subtitle">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="page-header-actions">{actions}</div>}
      </div>
      {children}
    </PageHead>
  );
}

// ---------------------------------------------------------------------------
// SectionTabs
// ---------------------------------------------------------------------------

/**
 * Sub-navigation inside one product area, for areas with a handful of pages
 * that are not organization settings and so do not belong in that rail.
 */
export function SectionTabs({ label, children }: { label: string; children: ReactNode }) {
  return (
    <nav className="section-tabs" aria-label={label}>
      {children}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Scrollable strips
// ---------------------------------------------------------------------------

/** Smooth where the platform offers it, instant where it does not (jsdom). */
function scrollStrip(el: HTMLElement, left: number) {
  const target = Math.max(0, left);
  if (typeof el.scrollTo === "function") el.scrollTo({ left: target, behavior: "smooth" });
  else el.scrollLeft = target;
}

/**
 * Tracks whether a horizontally scrollable row has more content past either
 * edge, and moves it by roughly a screenful.
 *
 * Measured rather than assumed: how far a row overflows depends on its contents
 * and on the width it is given, neither of which this hook can know. Pass
 * `deps` for anything that changes the contents, since a row that gained items
 * overflows without ever scrolling or resizing.
 */
export function useStripOverflow<T extends HTMLElement>(deps: unknown[] = []) {
  const ref = useRef<T | null>(null);
  const [more, setMore] = useState({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // A fractional scrollWidth can sit a hair above clientWidth with nothing
    // actually hidden, so a pixel of slack keeps a chevron from flickering in.
    setMore({ start: el.scrollLeft > 1, end: el.scrollWidth - el.clientWidth - el.scrollLeft > 1 });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  const nudge = useCallback((direction: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    scrollStrip(el, el.scrollLeft + direction * Math.max(150, el.clientWidth * 0.6));
  }, []);

  /** Bring one item fully into view, centred, without scrolling the page too. */
  const center = useCallback((item: HTMLElement | null) => {
    const el = ref.current;
    if (!el || !item) return;
    scrollStrip(el, item.offsetLeft - (el.clientWidth - item.offsetWidth) / 2);
    measure();
  }, [measure]);

  return { ref, more, nudge, center, measure };
}

/**
 * The affordance for a strip that scrolls. Out of the tab order on purpose:
 * the strip's own contents are already reachable by keyboard, so extra stops
 * before every item would be a cost with no matching gain.
 */
export function StripChevron({ back = false, className, label, disabled, onClick }: { back?: boolean; className: string; label?: string; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`${className} ${back ? "start" : "end"}`}
      onClick={onClick}
      disabled={disabled}
      tabIndex={-1}
      {...(label ? { "aria-label": back ? `${label} left` : `${label} right` } : { "aria-hidden": true })}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={back ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// DataTable
// ---------------------------------------------------------------------------

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: string | number;
  align?: "left" | "right" | "center";
  /** Goes on the header and every cell, for widths a table with many columns has to be told about. */
  className?: string;
  /** Written to `data-label` on each cell so stacked (phone) layouts can caption the value. */
  label?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowClassName,
  onRowClick,
  rowClickable,
  loading,
  loadingMore,
  empty,
  ariaLabel,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  rowClassName?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  /** When set, only matching rows open. The others stay inert. */
  rowClickable?: (row: T) => boolean;
  loading?: boolean;
  /** Appends a few skeleton rows under the ones already shown, for the next page. */
  loadingMore?: boolean;
  empty?: ReactNode;
  ariaLabel?: string;
}) {
  return (
      <table className="table" aria-label={ariaLabel} aria-busy={loading || loadingMore || undefined}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={c.className} style={{ width: c.width, textAlign: c.align }}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const opens = !!onRowClick && (!rowClickable || rowClickable(r));
          return (
          <tr
            key={rowKey(r)}
            data-row-id={rowKey(r)}
            onClick={opens ? () => onRowClick?.(r) : undefined}
            onKeyDown={opens ? (e) => (e.key === "Enter" ? onRowClick?.(r) : undefined) : undefined}
            tabIndex={opens ? 0 : undefined}
            className={[opens ? "clickable" : "static", rowClassName?.(r)].filter(Boolean).join(" ")}
          >
            {columns.map((c) => (
              <td key={c.key} className={c.className} style={{ textAlign: c.align }} data-label={c.label}>
                {c.render(r)}
              </td>
            ))}
          </tr>
          );
        })}
        {!loading && rows.length === 0 && (
          <tr className="static">
            <td colSpan={columns.length} className="muted" style={{ textAlign: "center", padding: 24 }}>
              {empty ?? "Nothing here yet."}
            </td>
          </tr>
        )}
        {loading &&
          Array.from({ length: 4 }).map((_, i) => (
            <tr key={`sk-${i}`} aria-hidden="true" className="static">
              {columns.map((c) => (
                <td key={c.key} className={c.className} data-label={c.label}>
                  <span className="skeleton">loading</span>
                </td>
              ))}
            </tr>
          ))}
        {loadingMore &&
          Array.from({ length: 3 }).map((_, i) => (
            <tr key={`more-${i}`} aria-hidden="true" className="static">
              {columns.map((c) => (
                <td key={c.key} className={c.className} data-label={c.label}>
                  <span className="skeleton">loading</span>
                </td>
              ))}
            </tr>
          ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// RowMenu
// ---------------------------------------------------------------------------

/**
 * Overflow menu for table rows. Children are the menu items: links or buttons
 * with role="menuitem". Any click inside the menu closes it.
 */
export function RowMenu({ label = "Row actions", children, testId }: { label?: string; children: ReactNode; testId?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="row-menu" ref={ref}>
      <button type="button" className="btn ghost icon-btn" aria-haspopup="menu" aria-expanded={open} aria-label={label} data-testid={testId} onClick={() => setOpen((o) => !o)}>
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="3.25" r="1.25" fill="currentColor" />
          <circle cx="8" cy="8" r="1.25" fill="currentColor" />
          <circle cx="8" cy="12.75" r="1.25" fill="currentColor" />
        </svg>
      </button>
      {open && (
        <div className="menu" role="menu" onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SearchSelect
// ---------------------------------------------------------------------------

export interface SearchSelectOption {
  value: string;
  label: string;
  /** Secondary text, shown at the right of the open row and omitted from the closed control. */
  detail?: string;
  /** A small picture before the label (a flag, a provider mark). Shown in the row and the closed control. */
  icon?: ReactNode;
  /** Extra words the search should match, beyond the label. */
  keywords?: string;
  disabled?: boolean;
}

/** Room the menu needs below the trigger before it flips above it instead. */
const SELECT_MENU_MAX_H = 296;

/**
 * A select whose open menu always starts with a search field. Native selects
 * become unusable once a provider returns hundreds of models, and using one
 * interaction everywhere avoids making people remember which dropdowns search.
 *
 * The menu is portalled and positioned in viewport coordinates because the
 * triggers sit inside cards and dialogs that clip their overflow; an absolutely
 * positioned menu gets cut off by whichever ancestor does that.
 */
export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = "Select an option",
  searchPlaceholder = "Search…",
  empty = "No matching options.",
  className = "",
  menuClassName = "",
  id,
  disabled,
  required,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  summary,
}: {
  value: string;
  options: SearchSelectOption[];
  onChange: (value: string) => void;
  /** What the closed control shows for the selected option, when not its label (a flag and dial code, say). */
  summary?: (selected: SearchSelectOption) => ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  empty?: string;
  className?: string;
  /** Extra class on the portalled menu, which is not inside the trigger. */
  menuClassName?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const auto = useId();
  const listId = `${id ?? auto}-listbox`;
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const [at, setAt] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(null);
  const [query, setQuery] = useState("");
  const open = !!at;
  const selected = options.find((option) => option.value === value);
  const needle = query.trim().toLocaleLowerCase();
  const visible = options.filter((option) => `${option.label} ${option.detail ?? ""} ${option.keywords ?? ""}`.toLocaleLowerCase().includes(needle));

  const close = useCallback(() => {
    setAt(null);
    setQuery("");
  }, []);
  const place = useCallback(() => {
    const rect = trigger.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 240);
    const left = Math.min(Math.max(rect.left, 8), Math.max(window.innerWidth - width - 8, 8));
    // Below unless that would run off the bottom and there is more room above.
    const room = window.innerHeight - rect.bottom;
    if (room < SELECT_MENU_MAX_H && rect.top > room) setAt({ left, width, bottom: window.innerHeight - rect.top + 4 });
    else setAt({ left, width, top: rect.bottom + 4 });
  }, []);

  useEffect(() => {
    if (!open) return;
    search.current?.focus();
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !menu.current?.contains(target)) close();
    };
    // Captured, so Escape closes only this menu: the dialog some of these live
    // in also closes on Escape, and would otherwise go with it.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
      trigger.current?.focus();
    };
    // The menu is positioned against the viewport, so anything that moves the
    // trigger has to move it too. A trigger in a sticky filter row does not
    // move at all while the page scrolls under it, and closing the menu there
    // would take it out from under the pointer for no reason; one that scrolls
    // away is gone, and its menu goes with it. Scrolling the list of options
    // must do neither: this is captured to see scrolls on elements that don't
    // bubble them, which includes the menu's own list.
    const onScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (target && menu.current?.contains(target)) return;
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) close();
      else place();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close, place]);

  return (
    <div className={`search-select ${className}`}>
      <button
        id={id}
        ref={trigger}
        type="button"
        value={value}
        className="input search-select-trigger"
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-controls={listId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => (open ? close() : place())}
      >
        {selected && summary ? (
          summary(selected)
        ) : (
          <>
            {selected?.icon && <span className="search-select-icon">{selected.icon}</span>}
            <span className={selected ? "" : "muted"}>{selected?.label ?? placeholder}</span>
          </>
        )}
      </button>
      {at &&
        createPortal(
          <div className={`search-select-pop ${menuClassName}`} ref={menu} style={{ left: at.left, top: at.top, bottom: at.bottom, width: at.width }}>
            <input
              ref={search}
              className="input search-select-search"
              type="search"
              value={query}
              aria-label={`Search ${ariaLabel ?? "options"}`}
              placeholder={searchPlaceholder}
              autoComplete="off"
              onChange={(event) => setQuery(event.target.value)}
            />
            <div id={listId} className="search-select-list" role="listbox" aria-label={ariaLabel}>
              {visible.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  value={option.value}
                  className="search-select-option"
                  role="option"
                  aria-selected={option.value === value}
                  disabled={option.disabled}
                  onClick={() => {
                    onChange(option.value);
                    close();
                    trigger.current?.focus();
                  }}
                >
                  {option.icon && <span className="search-select-icon">{option.icon}</span>}
                  <span className="search-select-label">{option.label}</span>
                  {option.detail && <span className="search-select-detail">{option.detail}</span>}
                </button>
              ))}
              {visible.length === 0 && <div className="search-select-empty">{empty}</div>}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hint
// ---------------------------------------------------------------------------

/** Half the bubble's maximum width plus a gutter, for keeping it on screen. */
const HINT_HALF = 132 + 8;

/**
 * A popover explaining the control it wraps, including a control that is
 * disabled. A native `title` cannot do that job: the browser shows no tooltip
 * for a disabled control, because such a control fires no events at all. So the
 * anchor listens instead, and CSS makes a disabled child transparent to hit
 * testing so hovers and taps reach the anchor. A tap toggles it, which a `title`
 * never offered to anyone on a touch screen.
 */
export function Hint({ text, children, testId, toggle = false }: { text: ReactNode; children: ReactNode; testId?: string; toggle?: boolean }) {
  const id = useId();
  const ref = useRef<HTMLSpanElement>(null);
  // The first press pins the explanation so it can be read. Hover previews it.
  // A second press, Escape, or a press elsewhere puts it away.
  const pinned = useRef(false);
  const [at, setAt] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const hide = useCallback(() => setAt(null), []);
  const dismiss = useCallback(() => {
    pinned.current = false;
    setAt(null);
  }, []);
  const show = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    // A pinned explanation on a metric card hangs off the card, so it does not
    // cover the number it is explaining or the top bar above the first row.
    const card = toggle ? ref.current?.closest(".metric-wrap")?.getBoundingClientRect() : undefined;
    const topEdge = card?.top ?? r.top;
    const bottomEdge = card?.bottom ?? r.bottom;
    // Centred on the anchor and clamped by the bubble's own half-width, so a
    // control near either edge keeps its explanation on screen. It sits above
    // unless that would run into the top bar.
    const below = topEdge < 64 + 140;
    setAt({ left: Math.min(Math.max(r.left + r.width / 2, HINT_HALF), window.innerWidth - HINT_HALF), top: below ? bottomEdge + 8 : topEdge - 8, below });
  }, [toggle]);
  useEffect(() => {
    if (!at) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && dismiss();
    // Anything that moves the anchor leaves the bubble pointing at nothing, and
    // re-measuring on every scroll frame is not worth it for a hover hint.
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    // Only while a tap pinned it: a hover hint should not close on the next
    // press, because that press is often the control the hint is explaining.
    const onDown = (e: PointerEvent) => {
      if (!pinned.current || ref.current?.contains(e.target as Node)) return;
      // A press on the bubble itself is handled there, so removing it on the
      // way down does not let the click fall through onto whatever it covered.
      if ((e.target as Element | null)?.closest?.(".hint-pop")) return;
      dismiss();
    };
    if (toggle) document.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
      if (toggle) document.removeEventListener("pointerdown", onDown);
    };
  }, [at, dismiss, toggle]);
  return (
    <span
      className="hint-anchor"
      ref={ref}
      onPointerEnter={(e) => {
        if (toggle && e.pointerType === "touch") return;
        show();
      }}
      onPointerLeave={() => {
        if (pinned.current) return;
        hide();
      }}
      onFocus={toggle ? show : undefined}
      onBlur={
        toggle
          ? () => {
              if (!pinned.current) hide();
            }
          : undefined
      }
      // Only a tap that landed on the anchor itself, so wrapping a working
      // control does not swallow half of its own clicks. A toggle hint is the
      // control: the first press pins the explanation so it can be read, and
      // the next press puts it away. Hover still previews it.
      onClick={(e) => {
        if (!toggle) {
          if (e.target === e.currentTarget) at ? hide() : show();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        pinned.current = !pinned.current;
        if (pinned.current) show();
        else hide();
      }}
      data-testid={testId}
    >
      {children}
      {/* A disabled control cannot be focused, so the explanation is read here
          instead of announced from a popover nobody can open with a keyboard. */}
      <span className="sr-only">{text}</span>
      {at &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            className={`hint-pop ${at.below ? "below" : ""} ${pinned.current ? "pinned" : ""}`}
            style={{ left: at.left, top: at.top }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (pinned.current) dismiss();
            }}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Automatic pagination
// ---------------------------------------------------------------------------

/**
 * Fetches the next page shortly before the user reaches the end of a list.
 * Re-observing after each completed request also fills short viewports until
 * either the page can scroll or the server has no more results.
 */
export function AutoLoadMore({
  hasMore,
  loading,
  loadMore,
  label = "Loading more results",
}: {
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
  label?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const loadRef = useRef(loadMore);
  loadRef.current = loadMore;
  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || loading || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadRef.current();
      },
      { rootMargin: "480px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  if (!hasMore && !loading) return null;
  return (
    <div ref={ref} className="auto-load-more" role="status" aria-live="polite">
      {loading ? <span className="muted small">{label}…</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  width = 640,
  danger,
  fullScreen,
  side,
  subtitle,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  danger?: boolean;
  /** For long records: takes the whole viewport rather than a box that scrolls inside it. */
  fullScreen?: boolean;
  /** For long editors: a full-height panel at the right edge, so the page stays in view behind it. */
  side?: boolean;
  subtitle?: ReactNode;
  className?: string;
}) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    // Prefer the dialog's task over its close affordance. Focusing the header's
    // X first made every modal open with a large focus ring around its least
    // important action.
    // A selector list resolves in DOM order, which would hand focus to the
    // header's X before the footer; query the footer on its own first.
    const first =
      ref.current?.querySelector<HTMLElement>(".dialog-body input, .dialog-body select, .dialog-body textarea, .dialog-body button") ??
      ref.current?.querySelector<HTMLElement>("footer button") ??
      ref.current?.querySelector<HTMLElement>("header button");
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className={`dialog-backdrop${fullScreen ? " full" : ""}${side ? " side" : ""}`} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`dialog${fullScreen ? " full" : ""}${side ? " side" : ""}${danger ? " danger" : ""}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={fullScreen || side ? undefined : { width }}
        ref={ref}
      >
        <header>
          <div className="dialog-heading">
            <h2 id={titleId}>{title}</h2>
            {subtitle && <p className="dialog-subtitle">{subtitle}</p>}
          </div>
          <button className="btn ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="dialog-body scroll-cue">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmDialog
// ---------------------------------------------------------------------------

/**
 * A modal yes/no. Replaces window.confirm so the question matches the rest of
 * the product and the confirm button can show a pending state.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger,
  busy,
  disabled,
  typeToConfirm = !!danger,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /** Keeps the confirm button off until the caller says the form is ready. */
  disabled?: boolean;
  /** Asks for CONFIRM to be typed first. On for every danger dialog unless the caller has its own typed check. */
  typeToConfirm?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);
  const blocked = disabled || (typeToConfirm && typed !== CONFIRM_WORD);
  const close = () => {
    if (!busy) onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      title={title}
      width={440}
      danger={danger}
      footer={
        <>
          <button type="button" className="btn" onClick={close} disabled={busy}>
            {cancelLabel}
          </button>
          <button type="button" className={`btn ${danger ? "danger" : "primary"}`} onClick={onConfirm} disabled={busy || blocked} autoFocus={!blocked} aria-busy={busy || undefined}>
            {busy && <span className="spinner" aria-hidden="true" />}
            {confirmLabel}
          </button>
        </>
      }
    >
      {children}
      {typeToConfirm && (
        <form
          className="confirm-typed"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy && !blocked) onConfirm();
          }}
        >
          <Field label={<>Type <code>{CONFIRM_WORD}</code> to continue</>}>
            {(p) => (
              <input
                {...p}
                className="input"
                value={typed}
                onChange={(event) => setTyped(event.target.value.toUpperCase())}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={CONFIRM_WORD.length}
                disabled={busy}
                aria-label={`Type ${CONFIRM_WORD} to confirm`}
              />
            )}
          </Field>
        </form>
      )}
    </Dialog>
  );
}

const CONFIRM_WORD = "CONFIRM";

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

export interface Toast {
  id: number;
  kind: "info" | "success" | "error";
  message: ReactNode;
}
interface ToastCtx {
  toasts: Toast[];
  push: (kind: Toast["kind"], message: ReactNode) => void;
  dismiss: (id: number) => void;
}
const ToastContext = createContext<ToastCtx | null>(null);
let toastSeq = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismiss = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (kind: Toast["kind"], message: ReactNode) => {
      const id = toastSeq++;
      setToasts((t) => [...t, { id, kind, message }]);
      setTimeout(() => dismiss(id), kind === "error" ? 8000 : 4000);
    },
    [dismiss],
  );
  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span>{t.message}</span>
            <button className="btn ghost" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Allow use outside a provider (tests) as a no-op.
    return { push: () => undefined, dismiss: () => undefined, toasts: [] as Toast[] };
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
  id,
  inline,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (props: { id: string; autoComplete: string; "aria-invalid"?: boolean; "aria-describedby"?: string }) => ReactNode;
  id?: string;
  inline?: boolean;
  className?: string;
}) {
  const auto = useId();
  const fid = id ?? auto;
  const hintId = `${fid}-hint`;
  const errId = `${fid}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={["field", inline ? "inline" : "", error ? "has-error" : "", className ?? ""].filter(Boolean).join(" ")}>
      <label htmlFor={fid}>{label}</label>
      {/* autoComplete defaults to off so browsers never resurface what a previous
          user typed on a shared machine. Credential fields (username, password,
          one-time-code, webauthn) set their own token after the spread; those are
          the contract password managers and passkey autofill are matched on. */}
      {children({ id: fid, autoComplete: "off", "aria-invalid": error ? true : undefined, "aria-describedby": describedBy })}
      {hint && (
        <div className="hint" id={hintId}>
          {hint}
        </div>
      )}
      {error && (
        <div className="error" id={errId} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * A toggle with an optional hint. The hint sits outside the label so it stays
 * out of the switch's accessible name and reads as a description instead.
 */
export function Switch({ checked, onChange, label, hint, id, disabled, title }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; id?: string; disabled?: boolean; title?: string }) {
  const auto = useId();
  const sid = id ?? auto;
  const hintId = `${sid}-hint`;
  const toggle = (
    <label className={`switch${disabled ? " disabled" : ""}`} htmlFor={sid} title={title}>
      <input id={sid} type="checkbox" role="switch" aria-checked={checked} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-describedby={hint ? hintId : undefined} autoComplete="off" />
      <span className="track" />
      <span>{label}</span>
    </label>
  );
  if (!hint) return toggle;
  return (
    <div className="switch-field">
      {toggle}
      <p className="switch-hint" id={hintId}>
        {hint}
      </p>
    </div>
  );
}

/**
 * Copy to clipboard. Icon only unless a label is given: the value being copied
 * sits right beside the button, so the word "Copy" only repeats what the icon
 * already says. A label is for the cases where the target is not obvious from
 * the surroundings — "Copy all", "Copy token".
 */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  const name = done ? "Copied" : label ?? "Copy";
  return (
    <button
      className={`btn ghost${label ? "" : " icon-btn"}`}
      type="button"
      // Titled either way: the icon-only form needs the name on hover, and a
      // labelled one still reads as "Copied" while the confirmation shows.
      title={name}
      aria-label={label ? undefined : name}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* ignore */
        }
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {done ? <path d="M20 6 9 17l-5-5" /> : <><rect x="8" y="8" width="14" height="14" rx="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></>}
      </svg>
      {label ? <span>{done ? "Copied" : label}</span> : null}
    </button>
  );
}

/**
 * Busy button. The label changes and a spinner runs, so a slow request looks
 * like work in progress rather than a click that did nothing.
 */
export function BusyButton({
  busy,
  busyLabel,
  children,
  className = "btn",
  ...rest
}: { busy: boolean; busyLabel: ReactNode; className?: string } & Omit<ComponentPropsWithoutRef<"button">, "className">) {
  return (
    <button {...rest} type={rest.type ?? "button"} className={className} disabled={busy || rest.disabled} aria-busy={busy || undefined}>
      {busy && <span className="spinner" aria-hidden="true" />}
      {busy ? busyLabel : children}
    </button>
  );
}

/**
 * A usage meter reads as three facts: what is included, what has been used, and
 * what the overage past the package costs. A meter with nothing included is not
 * unlimited — it is a plan that does not carry the feature — so it says so
 * rather than drawing an infinity sign over an empty package.
 */
export function Meter({
  label,
  used,
  included,
  projected,
  unit,
  format,
  overageUnits,
  overageUsd,
  overageUnitPrice,
  overagePer1000,
  overagePerMillion,
  status,
}: {
  label: ReactNode;
  used: number;
  included: number;
  projected?: number;
  unit?: string;
  format?: (n: number) => string;
  overageUnits?: number;
  overageUsd?: number;
  overageUnitPrice?: number;
  overagePer1000?: boolean;
  overagePerMillion?: boolean;
  /** What the feature is doing right now when it is not recording normally ("Stopped: cap reached"). */
  status?: string;
}) {
  const fmt = format ?? fmtNum;
  const over = Math.max(0, overageUnits ?? used - included);
  // Scaled to the largest of package, usage and projection so an overage stays
  // legible beside the package it ran past instead of pinning both at full.
  const scale = Math.max(included, used, projected ?? 0) || 1;
  const pct = (n: number) => Math.max(0, Math.min(100, (n / scale) * 100));
  const ratio = included > 0 ? used / included : used > 0 ? 1 : 0;
  const tone = over > 0 || ratio >= 1 ? "danger" : ratio >= 0.8 ? "warn" : "ok";
  const suffix = unit ? ` ${unit}` : "";
  const left = Math.max(0, included - used);
  return (
    <div className="meter card">
      <div className="meter-head">
        <strong>{label}</strong>
        <span className="muted">
          {included > 0 ? (
            <>
              {fmt(used)} / {fmt(included)}
              {suffix} included
            </>
          ) : (
            <>
              {fmt(used)}
              {suffix}
            </>
          )}
        </span>
      </div>
      <div className="meter-bar" role="progressbar" aria-valuemin={0} aria-valuemax={included || undefined} aria-valuenow={used}>
        <div className={`fill ${tone}`} style={{ width: `${pct(Math.min(used, included))}%` }} />
        {over > 0 && <div className="fill over" style={{ width: `${pct(over)}%` }} />}
        {projected != null && <div className="projected" style={{ left: `${pct(projected)}%` }} title={`Projected month end: ${fmt(projected)}`} />}
      </div>
      <div className="meter-foot">
        <span className="hint">{projected != null ? `Projected month end: ${fmt(projected)}` : ""}</span>
        {/* Nothing included and no price past it: the plan does not carry this,
            so usage against it is not an overage, it is off-plan entirely. */}
        {status ? (
          <span className="over-tag" role="status">{status}</span>
        ) : included <= 0 && !overageUnitPrice ? (
          <span className="hint">Not included on this plan</span>
        ) : over > 0 && fmt(over) !== fmt(0) ? (
          <span className="over-tag">
            +{fmt(over)}
            {suffix} over{overageUsd ? ` · ${fmtUsd(overageUsd)}` : ""}
          </span>
        ) : included > 0 ? (
          <span className="hint">
            {fmt(left)}
            {suffix} left
            {overageUnitPrice ? `, then ${rateLabel(overageUnitPrice, unit ?? "", overagePer1000, overagePerMillion)}` : ""}
          </span>
        ) : (
          <span className="hint">Billed from the first {unit ? singular(unit) : "unit"}</span>
        )}
      </div>
    </div>
  );
}

/**
 * fmtRate prices one unit. Overage rates run well under a cent — a stored
 * session is 1.5¢ — so rounding to the two digits an invoice total uses would
 * quote the wrong price.
 */
export function fmtRate(n: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(n);
}

/** Units are written plural on a meter and singular against a unit price. */
export function singular(unit: string): string {
  return unit.endsWith("s") && !unit.endsWith("ss") ? unit.slice(0, -1) : unit;
}

/**
 * Quotes an overage rate in the unit the plan prices it in. Sessions are sold
 * per thousand, so quoting that rate per session would be off by 1000x.
 */
export function rateLabel(price: number, unit: string, per1000 = false, perMillion = false): string {
  if (perMillion) return `${fmtRate(price)} per 1,000,000 ${unit}`;
  return per1000 ? `${fmtRate(price)} per 1,000 ${unit}` : `${fmtRate(price)} per ${singular(unit)}`;
}

export function fmtNum(n: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);
}

/**
 * fmtUsd shows money at a precision that matches its size: a $0.0042 AI call
 * and a $1,204 invoice both have to be readable.
 */
export function fmtUsd(n: number): string {
  const abs = Math.abs(n);
  const digits = abs === 0 ? 2 : abs < 0.01 ? 4 : 2;
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}
